import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { routes } from '../../../../../constants/routing';
import { BUILDER_ACTION_BUTTONS, BUILDER_EDIT_PAGE_TABS } from '../../../../../constants/templates';
import { routerProxyPush } from '../../../../../store/actions/shell';
import {
  addClaimLinkTemplateRequest,
  clearTemplateValidation,
  editClaimLinkTemplateRequest,
} from '../../../../../store/actions/templates';
import { selectDefaultExperienceBlueprint } from '../../../../../store/selectors/shell';
import {
  selectClaimLinkBuilderDraft,
  selectIsClaimLinkTemplateSaving,
  selectIsTemplateDraftDirty,
  selectTemplateValidationErrors,
} from '../../../../../store/selectors/templates';
import {
  ClaimLinkEditorPageEnum,
  ClaimLinkTemplateActionTypeEnum,
  ClaimLinkTemplateStatusEnum,
  IBuilderActionButtonConfig,
  IClaimLinkTemplate,
} from '../../../../../types/templates';
import { getCountLabel } from '../../../../../utils/helpers';
import notification from '../../../../../utils/notification';
import { getPageErrorCount, transformBuilderValuesToClaimLinkTemplate } from '../../../../../utils/templates';

interface IProps {
  activeTab: ClaimLinkEditorPageEnum;
  onTabChange: (value: ClaimLinkEditorPageEnum) => void;
  shouldShowError: (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum) => boolean;
  onOpenTemplateDefaults: (event: React.MouseEvent<HTMLElement>) => void;
}

export const EditorHeader = ({ activeTab, onTabChange, shouldShowError, onOpenTemplateDefaults }: IProps) => {
  const theme = useTheme();
  const { action, text } = theme.palette;
  const dispatch = useDispatch();
  const templateDraft = useSelector(selectClaimLinkBuilderDraft);
  const defaultBlueprint = useSelector(selectDefaultExperienceBlueprint);
  const validationErrors = useSelector(selectTemplateValidationErrors);
  const isDirty = useSelector(selectIsTemplateDraftDirty);
  const isSaving = useSelector(selectIsClaimLinkTemplateSaving);
  const { status, uid } = templateDraft;
  const isEditMode = Boolean(uid);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: ClaimLinkEditorPageEnum) => {
      if (value) {
        onTabChange(value);
      }
    },
    [onTabChange],
  );

  const handleSaveOrPublish = useCallback(
    (actionType: ClaimLinkTemplateActionTypeEnum) => {
      const statusOverride =
        actionType === ClaimLinkTemplateActionTypeEnum.Save
          ? ClaimLinkTemplateStatusEnum.Draft
          : ClaimLinkTemplateStatusEnum.Live;

      const draftWithStatus = {
        ...templateDraft,
        status: statusOverride,
      };

      const apiPayload = transformBuilderValuesToClaimLinkTemplate(draftWithStatus, defaultBlueprint, undefined);

      const { uid: _uid, ...templateWithoutUid } = apiPayload as Partial<IClaimLinkTemplate> & { uid?: string };

      // Check if we're editing an existing template or creating a new one
      const templateUid = templateDraft?.uid;
      const isEditing = Boolean(templateUid);

      const actionPayload = {
        uid: templateUid,
        template: templateWithoutUid,
        resolve: () => {
          notification.success(undefined, {
            content:
              actionType === ClaimLinkTemplateActionTypeEnum.Save
                ? 'Template saved successfully!'
                : 'Template published successfully!',
          });
          dispatch(routerProxyPush(routes.experienceManager.claimLink));
        },
        reject: (error: { message?: string }) => {
          notification.error('Save failed', {
            content: error?.message || 'Failed to save template',
          });
        },
      };

      const actionCreator = isEditing ? editClaimLinkTemplateRequest : addClaimLinkTemplateRequest;
      dispatch(actionCreator(actionPayload));
    },
    [templateDraft, defaultBlueprint, dispatch],
  );

  const handleValidateAndSave = useCallback(
    (actionType: ClaimLinkTemplateActionTypeEnum) => {
      dispatch(clearTemplateValidation());
      handleSaveOrPublish(actionType);
    },
    [dispatch, handleSaveOrPublish],
  );

  const actionButtonsWithHandlers = useMemo(() => {
    const actionMap: Record<string, () => void> = {
      save_draft_btn: () => handleValidateAndSave(ClaimLinkTemplateActionTypeEnum.Save),
      publish_changes_btn: () => handleValidateAndSave(ClaimLinkTemplateActionTypeEnum.Publish),
    };

    return BUILDER_ACTION_BUTTONS.map((button) => ({
      ...button,
      onClick: actionMap[button.dataAction] || button.onClick,
    }));
  }, [handleValidateAndSave]);

  const handleBackToList = useCallback(() => {
    dispatch(routerProxyPush(routes.experienceManager.claimLink));
  }, [dispatch]);

  const renderActionButton = useCallback(
    (config: IBuilderActionButtonConfig) => {
      const { label, variant, dataAction, onClick, visibleWhen } = config;

      if (visibleWhen && status !== visibleWhen) {
        return null;
      }

      const isSaveDraft = dataAction === 'save_draft_btn';
      const isPublishChanges = dataAction === 'publish_changes_btn';
      const isActive = status === ClaimLinkTemplateStatusEnum.Live;

      // When no changes, show "Back to List" instead of "Publish changes"
      if (isEditMode && isPublishChanges && isActive && !isDirty) {
        return (
          <Button
            key={dataAction}
            variant={variant}
            size="small"
            sx={{ minWidth: 115 }}
            onClick={handleBackToList}
            data-action="back_to_list_btn"
          >
            Back to list
          </Button>
        );
      }

      const isDisabled = isSaving || (isEditMode && isSaveDraft && !isDirty);

      return (
        <Button
          key={dataAction}
          variant={variant}
          size="small"
          onClick={onClick}
          data-action={dataAction}
          disabled={isDisabled}
          sx={isPublishChanges ? { minWidth: 115 } : undefined}
        >
          {label}
        </Button>
      );
    },
    [status, isSaving, isEditMode, isDirty, handleBackToList],
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 42 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Button
          variant="outlined"
          onClick={onOpenTemplateDefaults}
          size="small"
          startIcon={<TuneIcon sx={{ color: action.active }} />}
          sx={{ borderColor: action.active, color: text.primary, flexShrink: 0 }}
          data-action="template_defaults_btn"
        >
          Template defaults
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color={theme.palette.text.secondary}>
            Edit page:
          </Typography>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ minHeight: 42, '& .MuiButtonBase-root.MuiTab-root': { minHeight: 42 } }}
            data-action="editor_tabs"
          >
            {BUILDER_EDIT_PAGE_TABS.map((tab) => {
              const { value, label, dataAction } = tab;
              const errorCount = getPageErrorCount(value, validationErrors, shouldShowError);
              const hasError = errorCount > 0;

              return (
                <Tab
                  key={value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {label}
                      {hasError && (
                        <Tooltip title={`${getCountLabel(errorCount, 'issue', 'issues')} on this page`} arrow>
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  }
                  value={value}
                  data-action={dataAction}
                />
              );
            })}
          </Tabs>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {actionButtonsWithHandlers.map(renderActionButton)}
      </Box>
    </Box>
  );
};
